import TurndownService from 'turndown';
// @ts-expect-error -- turndown-plugin-gfm has no published types; cast via TurndownService.Plugin below
import { gfm as untypedGfm } from 'turndown-plugin-gfm';
import {
  escapeMarkdownCharactersIfNeeded,
  fixBreaksBeforeLists,
  fixNestedListsAndWhitespaceInEmphasisNodes,
  getAlignment,
} from './utils';
import { isImageSizePreset } from '../imageSizes';

const gfm: TurndownService.Plugin = untypedGfm;

const BR_MARKER = '§br§';

const getContentWithoutBackticks = (content: string) => {
  return content.replace(/^([\s]*?)`([\s\S]*?)`([\s]*?)$/g, '$1$2$3');
};

const encodeParenthesesInUrl = (url: string): string => {
  return url.replace(/\(/g, '%28').replace(/\)/g, '%29');
};

// Equivalent to `HTMLFontElement.color`, which reflects the attribute, but also
// works in happy-dom, which doesn't implement `HTMLFontElement`.
const getFontElementColor = (element: HTMLElement): string | null =>
  element.nodeName === 'FONT' ? element.getAttribute('color') : null;

const buildImageSizeSuffix = (img: HTMLImageElement): string => {
  const size = img.getAttribute('data-size');
  if (isImageSizePreset(size)) {
    return size === 'large' ? '' : ` =${size}`;
  }
  const width = img.getAttribute('width');
  const height = img.getAttribute('height');
  if (width && height) return ` =${width}x${height}`;
  return '';
};

const buildService = (): TurndownService => {
  const service = new TurndownService({
    bulletListMarker: '*',
    codeBlockStyle: 'fenced',
    emDelimiter: '_',
    strongDelimiter: '**',
    headingStyle: 'atx',
    blankReplacement: (_content, node) => {
      const block = (node as HTMLElement & { isBlock?: boolean }).isBlock;
      if (block) return '\n\n';
      const text = node.textContent || '';
      return /[ \r\n\t]$/.test(text) ? ' ' : '';
    },
  });
  service.use(gfm);

  service.escape = (input: string) => input;

  service.addRule('strikethrough-double-tilde', {
    filter: node => ['DEL', 'S', 'STRIKE'].includes(node.nodeName),
    replacement: content => `~~${content}~~`,
  });

  service.addRule('list-no-double-break-inside-li', {
    filter: ['ul', 'ol'],
    replacement: (content, node) => {
      const parent = node.parentNode as HTMLElement | null;
      if (parent?.nodeName === 'LI') {
        return `\n${content}`;
      }
      return `\n\n${content}\n\n`;
    },
  });

  service.addRule('inline-code-with-nested-html', {
    filter: 'code',
    replacement: (_content, node) => {
      return Array.from(node.childNodes).reduce((acc, child) => {
        const trimedAcc = acc.substring(0, acc.length - 1);
        if (child.nodeType === Node.TEXT_NODE) {
          return `${trimedAcc}${child.nodeValue}\``;
        }
        if (child.nodeType === Node.ELEMENT_NODE) {
          return `${trimedAcc}\`${htmlToMarkdown(
            (child as HTMLElement).outerHTML
          )}\`\``;
        }
        return acc;
      }, '``');
    },
  });

  service.addRule('br-marker', {
    filter: 'br',
    replacement: () => BR_MARKER,
  });

  service.addRule('anchor-with-comp-links', {
    filter: 'a',
    replacement: (_content, node) => {
      const element = node as HTMLAnchorElement;
      const text = element.textContent;
      const title = element.title;
      const titleAttr = title ? ` "${title}"` : '';
      const href = element.getAttribute('href');

      if (element.classList.contains('inlineComp')) {
        const idRegex = {
          matcher: /.*\/workspace\/(.*)\/component\/(.*)/gm,
          match: 0,
          id: 2,
          wsid: 1,
        };
        const result = idRegex.matcher.exec(href || '');
        const id = result && result[idRegex.id];
        const wsid = result && result[idRegex.wsid];

        if (wsid) {
          return `[${text}](comp://${wsid}/${id}${titleAttr})`;
        }
      }
      return `[${text}](${encodeParenthesesInUrl(href || '')}${titleAttr})`;
    },
  });

  service.addRule('color-attrs', {
    filter: node => node.nodeName === 'FONT' || node.nodeName === 'SPAN',
    replacement: (innerHtml, node) => {
      const element = node as HTMLElement;
      const foregroundColor =
        element.style.color || getFontElementColor(element);
      const backgroundColor = element.style.backgroundColor;

      if (!foregroundColor && !backgroundColor) {
        return innerHtml;
      }

      const attributes = [foregroundColor || '', backgroundColor || ''].join(
        '/'
      );

      return `{color:${attributes}}${innerHtml}{color}`;
    },
  });

  service.addRule('iframe-vimeo-youtube', {
    filter: 'iframe',
    replacement: (_content, node) => {
      const src = (node as HTMLIFrameElement).getAttribute('src');
      const iframeRegex = /.+(vimeo|youtube).*\/(.*)/gm;
      const iframeParams = iframeRegex.exec(src || '');

      return `[${iframeParams![1]}:${iframeParams![2]}]`;
    },
  });

  service.addRule('th-cell', {
    filter: 'th',
    replacement: content => {
      return `| ${content.trim()} `;
    },
  });

  service.addRule('td-cell', {
    filter: 'td',
    replacement: content => {
      return `| ${content.trim()} `;
    },
  });

  service.addRule('tr-row', {
    filter: 'tr',
    replacement: (content, node) => {
      const cells = content.split('|').slice(1);
      const formattedCells = cells.map(cell => cell.trim()).join(' | ');
      const row = `| ${formattedCells} |`;
      const element = node as HTMLTableRowElement;
      if (element?.parentNode?.nodeName === 'THEAD') {
        const alignments = Array.from(element.children).map(child =>
          getAlignment(child.getAttribute('align'))
        );
        const alignmentRow = `| ${alignments.join(' | ')} |`;
        return `\n${row}\n${alignmentRow}`;
      } else if (element.children[0]?.nodeName === 'TH') {
        const alignments = Array.from(element.children).map(child =>
          getAlignment(child.getAttribute('align'))
        );
        const alignmentRow = `| ${alignments.join(' | ')} |`;
        return `\n${row}\n${alignmentRow}`;
      }
      return `\n${row}`;
    },
  });

  service.addRule('table', {
    filter: 'table',
    replacement: content => {
      return `\n${content}\n`;
    },
  });

  service.addRule('img-with-dims', {
    filter: 'img',
    replacement: (_content, node) => {
      const img = node as HTMLImageElement;
      const alt = img.alt || '';
      const src = img.getAttribute('src') || '';
      const title = img.title || '';
      const titlePart = title ? ` "${title}"` : '';
      const dimPart = buildImageSizeSuffix(img);
      return src ? `![${alt}]` + `(${src}${dimPart}${titlePart})` : '';
    },
  });

  service.addRule('pre-code-fenced-with-lang', {
    filter: node =>
      node.nodeName === 'PRE' && node.firstChild?.nodeName === 'CODE',
    replacement: (_content, node) => {
      const codeNode = (node as HTMLElement).firstChild as HTMLElement;
      const classNames = codeNode.getAttribute('class');
      const langMatch = classNames && classNames.match(/lang-(\S+)/);
      const lang = langMatch ? langMatch[1] : '';
      return `\n\n\`\`\`${lang}\n${getContentWithoutBackticks(
        codeNode.textContent || ''
      )}\n\`\`\`\n\n`;
    },
  });

  service.addRule('pre-fenced-plain', {
    filter: node =>
      node.nodeName === 'PRE' && node.firstChild?.nodeName !== 'CODE',
    replacement: (_content, node) => {
      const container = document.createElement('div');
      container.innerHTML = (node as HTMLElement).innerHTML.replace(
        /<br\s*\/?>/gim,
        BR_MARKER
      );
      return `\n\n\`\`\`\n${container.textContent!.replace(
        new RegExp(BR_MARKER, 'gm'),
        '\n'
      )}\n\`\`\`\n\n`;
    },
  });

  return service;
};

const htmlToMarkdown = (html: string): string => {
  const nodeFixed = fixNestedListsAndWhitespaceInEmphasisNodes(html);
  const listsFixed = fixBreaksBeforeLists(nodeFixed);
  const escapedText = escapeMarkdownCharactersIfNeeded(listsFixed);
  const service = buildService();
  const markdown = service.turndown(escapedText);
  return markdown
    .replace(/^[\t\r\n]+|[\t\r\n\s]+$/g, '')
    .replace(/\n\s+\n/g, '\n\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(new RegExp(BR_MARKER, 'gm'), '  \n')
    .replace(/ /gm, ' ');
};

export { htmlToMarkdown };
